import React, { useEffect, useRef } from 'react';

/**
 * RouteMap Component
 * Displays an embedded map with routes drawn from point A to B to C
 * Uses AWS Location Service + MapLibre GL JS
 *
 * Props:
 * - routes: Object with a_to_b and b_to_c route data
 *   - Each route contains: { origin: [lng, lat], destination: [lng, lat], polyline: [[lng, lat], ...], duration_minutes, distance_km }
 * - height: Map height (default: '300px')
 */
export default function RouteMap({ routes, height = '300px' }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    // Check if MapLibre is loaded
    if (!window.maplibregl) {
      console.error('MapLibre GL JS not loaded');
      return;
    }

    // Get AWS credentials from meta tags
    const awsApiKey = document.querySelector('meta[name="aws-maps-api-key"]')?.content;
    const awsRegion = document.querySelector('meta[name="aws-region"]')?.content || 'us-east-1';

    if (!awsApiKey) {
      console.error('AWS Maps API key not found in meta tags');
      return;
    }

    if (!routes) {
      return;
    }

    const normalizeCoord = (coord) => {
      if (!Array.isArray(coord) || coord.length < 2) return null;

      const lng = Number(coord[0]);
      const lat = Number(coord[1]);

      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
      return [lng, lat];
    };

    const normalizedRoutes = {
      a_to_b: routes.a_to_b ? {
        ...routes.a_to_b,
        origin: normalizeCoord(routes.a_to_b.origin),
        destination: normalizeCoord(routes.a_to_b.destination),
        polyline: Array.isArray(routes.a_to_b.polyline)
          ? routes.a_to_b.polyline.map(normalizeCoord).filter(Boolean)
          : [],
      } : null,
      b_to_c: routes.b_to_c ? {
        ...routes.b_to_c,
        origin: normalizeCoord(routes.b_to_c.origin),
        destination: normalizeCoord(routes.b_to_c.destination),
        polyline: Array.isArray(routes.b_to_c.polyline)
          ? routes.b_to_c.polyline.map(normalizeCoord).filter(Boolean)
          : [],
      } : null,
    };

    // Get center point (Point B - the recommendation location)
    const pointB = normalizedRoutes.a_to_b?.destination || normalizedRoutes.b_to_c?.origin;
    if (!pointB) {
      console.warn('No valid point B found for centering map');
      return;
    }

    // Initialize MapLibre map with AWS Location Service tiles
    let map;
    try {
      map = new window.maplibregl.Map({
        container: mapRef.current,
        style: `https://maps.geo.${awsRegion}.amazonaws.com/v2/styles/Standard/descriptor?key=${awsApiKey}`,
        center: [pointB[0], pointB[1]], // [lng, lat]
        zoom: 12,
        attributionControl: true,
        scrollZoom: false // Disable scroll to zoom (prevents accidental zooming while scrolling page)
      });
    } catch (error) {
      console.error('Failed to initialize route map:', error);
      return;
    }

    mapInstanceRef.current = map;

    map.on('load', () => {
      // Add route A→B (blue line)
      if (normalizedRoutes.a_to_b?.polyline && normalizedRoutes.a_to_b.polyline.length > 0) {
        map.addSource('route-a-to-b', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: normalizedRoutes.a_to_b.polyline // [[lng, lat], [lng, lat], ...]
            }
          }
        });

        map.addLayer({
          id: 'route-a-to-b-layer',
          type: 'line',
          source: 'route-a-to-b',
          paint: {
            'line-color': '#3B82F6', // Blue
            'line-width': 4,
            'line-opacity': 0.8
          }
        });

        // Add marker A (previous job)
        new window.maplibregl.Marker({ color: '#3B82F6' })
          .setLngLat([normalizedRoutes.a_to_b.origin[0], normalizedRoutes.a_to_b.origin[1]])
          .setPopup(
            new window.maplibregl.Popup({ offset: 25 }).setHTML(
              '<div class="text-sm font-medium">Previous Job (A)</div>'
            )
          )
          .addTo(map);
      }

      // Add route B→C (green line)
      if (normalizedRoutes.b_to_c?.polyline && normalizedRoutes.b_to_c.polyline.length > 0) {
        map.addSource('route-b-to-c', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: normalizedRoutes.b_to_c.polyline
            }
          }
        });

        map.addLayer({
          id: 'route-b-to-c-layer',
          type: 'line',
          source: 'route-b-to-c',
          paint: {
            'line-color': '#10B981', // Green
            'line-width': 4,
            'line-opacity': 0.8
          }
        });

        // Add marker C (next job)
        new window.maplibregl.Marker({ color: '#10B981' })
          .setLngLat([normalizedRoutes.b_to_c.destination[0], normalizedRoutes.b_to_c.destination[1]])
          .setPopup(
            new window.maplibregl.Popup({ offset: 25 }).setHTML(
              '<div class="text-sm font-medium">Next Job (C)</div>'
            )
          )
          .addTo(map);
      }

      // Add marker B (red - the recommendation / this job)
      if (pointB) {
        new window.maplibregl.Marker({ color: '#EF4444' })
          .setLngLat([pointB[0], pointB[1]])
          .setPopup(
            new window.maplibregl.Popup({ offset: 25 }).setHTML(
              '<div class="text-sm font-medium">This Job (B)</div>'
            )
          )
          .addTo(map);
      }

      // Fit map to show all points with padding
      const bounds = new window.maplibregl.LngLatBounds();

      if (normalizedRoutes.a_to_b?.origin) {
        bounds.extend(normalizedRoutes.a_to_b.origin);
      }

      if (pointB) {
        bounds.extend(pointB);
      }

      if (normalizedRoutes.b_to_c?.destination) {
        bounds.extend(normalizedRoutes.b_to_c.destination);
      }

      // Only fit bounds if we have at least 2 points
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, {
          padding: 50,
          maxZoom: 14 // Don't zoom in too close
        });
      }
    });

    map.on('error', (e) => {
      console.error('MapLibre error:', e);
    });

    // Cleanup on unmount
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [routes]);

  if (!routes) {
    return (
      <div
        className="bg-slate-100 rounded p-4 text-center text-slate-500"
        style={{ height }}
      >
        No route data available
      </div>
    );
  }

  return (
    <div
      ref={mapRef}
      style={{ height, width: '100%' }}
      className="rounded border border-slate-200"
    />
  );
}
